<template>
  <div class="group-form">
    <section class="group-card" data-cy="group-details">
      <h3 class="group-card__title">Group Details</h3>
      <p class="group-card__subtitle">Name this automation group internally.</p>

      <b-form-group
        label="Group Name"
        label-for="group-name"
        label-class="group-card__label"
      >
        <b-form-input
          id="group-name"
          v-model="form.name"
          type="text"
          class="group-card__control group-card__control--name"
          data-cy="group-name"
        />
      </b-form-group>
    </section>

    <section class="group-card" data-cy="group-filters">
      <h3 class="group-card__title">Filters</h3>
      <p class="group-card__subtitle">
        An account joins this group only when it matches every condition below.
        Conditions are re-evaluated on the schedule you set.
      </p>

      <div
        v-for="(row, index) in form.filters"
        :key="row.key"
        class="filter-row"
        :data-cy="`filter-row-${index}`"
      >
        <span class="filter-row__keyword">{{
          index === 0 ? 'Where' : 'And'
        }}</span>
        <b-form-select
          v-model="row.field"
          :options="fieldOptions"
          class="filter-row__field"
        />
        <span class="filter-row__muted">between</span>
        <div class="filter-row__values">
          <b-form-input
            v-model.number="row.min"
            type="number"
            :min="row.field === 'due' ? 0 : 1"
            placeholder="From"
            class="filter-row__value"
          />
          <span class="filter-row__muted">–</span>
          <b-form-input
            v-model.number="row.max"
            type="number"
            :min="row.field === 'due' ? 0 : 1"
            placeholder="To"
            class="filter-row__value"
          />
        </div>
        <button
          type="button"
          class="filter-row__remove"
          title="Remove filter"
          :data-cy="`filter-remove-${index}`"
          @click="removeFilter(index)"
        >
          ×
        </button>
      </div>

      <button
        type="button"
        class="add-filter"
        data-cy="add-filter"
        @click="addFilter"
      >
        + Add Filter
      </button>

      <p class="match-count" data-cy="match-count">
        <template v-if="matchCount === null"
          >Add a filter to see how many accounts match.</template
        >
        <template v-else>
          <strong>{{ matchCount.toLocaleString() }} accounts</strong> currently
          match these filters.
        </template>
      </p>
    </section>

    <section class="group-card group-card--split" data-cy="group-automation">
      <div class="group-card__half">
        <h3 class="group-card__title">Assign Templates</h3>
        <p class="group-card__subtitle">
          The message sent to accounts in this group.
        </p>

        <b-form-group
          label="Template"
          label-for="group-template"
          label-class="group-card__label"
        >
          <b-form-select
            id="group-template"
            v-model="form.templateId"
            :options="templateOptions"
            class="group-card__control"
            data-cy="group-template"
          >
            <template #first>
              <b-form-select-option :value="null" disabled
                >Select a template</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Read-only: the template's text is edited on the Templates page, never here. -->
        <div
          v-if="selectedTemplateText !== null"
          class="template-preview"
          data-cy="group-template-preview"
        >
          <span class="group-card__label">Message</span>
          <!-- v-text, not {{ }} on its own line: pre-wrap renders the template's indent as a leading space. -->
          <p
            class="template-preview__text"
            v-text="
              selectedTemplateText || 'This template has no text message.'
            "
          />
          <span class="template-preview__note"
            >Edit this text on the Templates page.</span
          >
        </div>
      </div>

      <div class="group-card__half">
        <h3 class="group-card__title">Automation Schedule</h3>
        <p class="group-card__subtitle">When matched accounts are messaged.</p>

        <div class="schedule-presets">
          <button
            v-for="preset in presets"
            :key="preset.label"
            type="button"
            class="schedule-chip schedule-chip--pill"
            :class="{ 'schedule-chip--active': isPreset(preset.days) }"
            :data-cy="`schedule-preset-${preset.label.toLowerCase()}`"
            @click="form.days = [...preset.days]"
          >
            {{ preset.label }}
          </button>
        </div>

        <div class="schedule-days" role="group" aria-label="Days of the week">
          <button
            v-for="day in dayButtons"
            :key="day.value"
            type="button"
            class="schedule-chip schedule-chip--day"
            :class="{ 'schedule-chip--active': form.days.includes(day.value) }"
            :aria-pressed="String(form.days.includes(day.value))"
            :title="day.title"
            :data-cy="`schedule-day-${day.value}`"
            @click="toggleDay(day.value)"
          >
            {{ day.letter }}
          </button>
        </div>

        <b-form-group
          label="Send Time"
          label-for="group-send-time"
          label-class="group-card__label"
        >
          <b-form-select
            id="group-send-time"
            v-model="form.sendTime"
            :options="sendTimeChoices"
            class="group-card__control group-card__control--time"
            data-cy="group-send-time"
          />
        </b-form-group>
      </div>
    </section>

    <footer class="group-card group-footer" data-cy="group-footer">
      <span class="group-footer__note">
        By saving, you confirm you have consent to message the accounts this
        group matches.
      </span>
      <div class="group-footer__actions">
        <b-button
          v-if="group && group.id"
          variant="outline-danger"
          data-cy="group-delete"
          :disabled="saving"
          @click="confirmDelete"
        >
          Delete Group
        </b-button>
        <b-button
          variant="outline-secondary"
          data-cy="group-cancel"
          :disabled="saving"
          @click="$emit('cancel')"
        >
          Cancel
        </b-button>
        <b-button
          variant="blue"
          data-cy="group-save"
          :disabled="saving"
          @click="save"
        >
          Save Group
        </b-button>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

/** The group row handed over from the list; null when creating. */
interface GroupSeed {
  id?: string
  pkid?: number
  name?: string
  filter?: Record<string, unknown>
  automation?: {
    template_id: number | null
    is_active: boolean
    days_of_week: number[]
    send_time: string
  } | null
}

interface FilterRow {
  key: number
  field: 'overdue' | 'due'
  min: number | null
  max: number | null
}

let rowKey = 0

interface TemplateBody {
  language: string
  body_type: string
  message_body: string
}

// The body the template editor shows and saves: English, text message.
function englishTextBody(bodies: TemplateBody[] = []): string {
  return (
    bodies.find(body => body.language === 'en-US' && body.body_type === 'TEXT')
      ?.message_body ?? ''
  )
}

// The two ranges the backend evaluates: days past due, and Django's due_date_range (0 = due today).
const FIELD_OPTIONS = [
  { value: 'overdue', text: 'Days past due' },
  { value: 'due', text: 'Days until due' }
]

// Stored 0 = Monday … 6 = Sunday (the BlytzCollect convention); shown Sun..Sat.
const DAY_BUTTONS = [
  { value: 6, letter: 'S', title: 'Sunday' },
  { value: 0, letter: 'M', title: 'Monday' },
  { value: 1, letter: 'T', title: 'Tuesday' },
  { value: 2, letter: 'W', title: 'Wednesday' },
  { value: 3, letter: 'T', title: 'Thursday' },
  { value: 4, letter: 'F', title: 'Friday' },
  { value: 5, letter: 'S', title: 'Saturday' }
]
const PRESETS = [
  { label: 'Daily', days: [0, 1, 2, 3, 4, 5, 6] },
  { label: 'Weekdays', days: [0, 1, 2, 3, 4] }
]

/** A pkid for core. A numeric string still counts; a UUID does not. */
function positivePkid(value: unknown): number | null {
  const pkid = typeof value === 'number' ? value : Number(value)
  if (!Number.isSafeInteger(pkid) || pkid <= 0) return null

  return pkid
}

/** `HH:MM` → `h:MM AM/PM`. */
function timeLabel(value: string): string {
  const [hour, minute] = value.split(':').map(Number)

  return `${((hour + 11) % 12) + 1}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`
}

/** 8:00 AM to 7:45 PM in 15-minute steps. */
const SEND_TIME_OPTIONS = Array.from({ length: 48 }, (_, step) => {
  const minutes = 8 * 60 + step * 15
  const value = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

  return { value, text: timeLabel(value) }
})

@Component({ computed: { ...mapGetters(['currentMerchant']) } })
export default class GroupForm extends Vue {
  @Prop({ type: Object, default: null }) group!: GroupSeed | null
  /** Pre-selects a template pkid when opened from a template's "+ Add Automated Text". */
  @Prop({ type: Number, default: null }) templateId!: number | null

  currentMerchant!: { id: string; pkid: number }

  saving = false
  fieldOptions = FIELD_OPTIONS
  dayButtons = DAY_BUTTONS
  presets = PRESETS
  templateOptions: Array<{ value: number; text: string; body: string }> = []
  matchCount: number | null = null
  private countTimer: ReturnType<typeof setTimeout> | null = null

  form = {
    name: this.group?.name ?? '',
    filters: GroupForm.rowsFromFilter(this.group?.filter),
    templateId: this.group?.automation?.template_id ?? this.templateId ?? null,
    days: [...(this.group?.automation?.days_of_week ?? [])],
    sendTime: this.group?.automation?.send_time ?? '08:00'
  }

  /** Editor rows from a saved filter: `due_date_range` "a,b", or `overdue_min` / `overdue_max`. */
  static rowsFromFilter(filter?: Record<string, unknown>): FilterRow[] {
    if (typeof filter?.due_date_range === 'string') {
      const [a, b] = filter.due_date_range.split(',').map(Number)

      return [
        {
          key: ++rowKey,
          field: 'due',
          min: Math.min(a, b),
          max: Math.max(a, b)
        }
      ]
    }
    const { overdue_min: min, overdue_max: max } = filter ?? {}
    if (min === undefined && max === undefined) return []

    return [
      {
        key: ++rowKey,
        field: 'overdue',
        min: min === undefined ? null : Number(min),
        max: max === undefined ? null : Number(max)
      }
    ]
  }

  /** The saved-filter JSON: the account-search params Django already accepts. */
  get filterJson(): Record<string, number | string> {
    const json: Record<string, number | string> = {}
    for (const row of this.form.filters) {
      if (row.field === 'due') {
        // Django needs both ends; a single value means that one day.
        const low = row.min ?? row.max
        const high = row.max ?? row.min
        if (low !== null && high !== null)
          json.due_date_range = `${low},${high}`
      } else {
        if (row.min) json.overdue_min = row.min
        if (row.max) json.overdue_max = row.max
      }
    }

    return json
  }

  /** The 15-minute grid, plus the saved time when it is off-grid (converted reminders keep 08:05). */
  get sendTimeChoices() {
    const time = this.form.sendTime
    if (!time || SEND_TIME_OPTIONS.some(option => option.value === time)) {
      return SEND_TIME_OPTIONS
    }

    return [{ value: time, text: timeLabel(time) }, ...SEND_TIME_OPTIONS].sort(
      (x, y) => x.value.localeCompare(y.value)
    )
  }

  /** The chosen template's English text; null until a template is chosen and loaded. */
  get selectedTemplateText(): string | null {
    const option = this.templateOptions.find(
      template => template.value === this.form.templateId
    )

    return option ? option.body : null
  }

  /** The first thing wrong with the form, or null when it can be saved. */
  get validationError(): string | null {
    if (!this.form.name.trim()) return 'Give the group a name.'
    // The backend evaluates one range; a mixed filter would match nobody.
    if (new Set(this.form.filters.map(row => row.field)).size > 1) {
      return 'Use either days past due or days until due, not both.'
    }
    if (!Object.keys(this.filterJson).length) return 'Add a filter.'
    if (!positivePkid(this.form.templateId)) return 'Choose a template.'
    if (!this.form.days.length) return 'Pick at least one day.'

    return null
  }

  mounted() {
    this.refreshMatchCount()
    this.loadTemplates()
  }

  addFilter() {
    this.form.filters.push({
      key: ++rowKey,
      field: 'overdue',
      min: null,
      max: null
    })
  }

  removeFilter(index: number) {
    this.form.filters.splice(index, 1)
  }

  isPreset(days: number[]): boolean {
    const chosen = [...this.form.days].sort()

    return (
      chosen.length === days.length && chosen.every((d, i) => d === days[i])
    )
  }

  toggleDay(day: number) {
    this.form.days = this.form.days.includes(day)
      ? this.form.days.filter(d => d !== day)
      : [...this.form.days, day].sort()
  }

  async loadTemplates() {
    try {
      const response = await this.axios.get(
        endpoints.listMessageTemplates(this.currentMerchant.id),
        { params: { page: 1, page_size: 1000 } }
      )
      this.templateOptions = (response.data?.results ?? []).map(
        (template: {
          pkid: number
          name: string
          message_bodies?: TemplateBody[]
        }) => ({
          value: template.pkid,
          text: template.name,
          body: englishTextBody(template.message_bodies)
        })
      )
    } catch {
      this.templateOptions = []
    }
  }

  // The same account search the Customers page runs, asked for one row.
  @Watch('filterJson', { deep: true })
  refreshMatchCount() {
    if (this.countTimer) clearTimeout(this.countTimer)
    const params = this.filterJson
    if (!Object.keys(params).length) {
      this.matchCount = null

      return
    }
    this.countTimer = setTimeout(async () => {
      try {
        const response = await this.axios.post(
          endpoints.listMerchantCustomerAccounts(this.currentMerchant.id),
          { customers: [] },
          { params: { ...params, page: 1, page_size: 1 } }
        )
        this.matchCount = response.data?.count ?? null
      } catch {
        this.matchCount = null
      }
    }, 400)
  }

  async confirmDelete() {
    const group = this.group
    if (!group?.id) return
    const ok = await this.$bvModal.msgBoxConfirm(
      `Delete "${group.name}"? Its automation stops with it.`,
      { title: 'Delete group', okVariant: 'danger', okTitle: 'Delete' }
    )
    if (!ok) return
    this.saving = true
    try {
      await this.axios.delete(
        endpoints.updateCustomerGroup(this.currentMerchant.id, group.id)
      )
      this.$emit('deleted')
    } catch {
      this.$bvToast.toast('Failed to delete the group', {
        title: 'Groups',
        variant: 'danger'
      })
    } finally {
      this.saving = false
    }
  }

  /** Django takes the group UUID. Core takes the group pkid, and template_id as a number. */
  async save() {
    const problem = this.validationError
    if (problem) {
      this.$bvToast.toast(problem, { title: 'Groups', variant: 'warning' })

      return
    }
    const templatePkid = positivePkid(this.form.templateId)
    if (templatePkid === null) return
    this.saving = true
    try {
      const body = { name: this.form.name.trim(), filter: this.filterJson }
      const djangoId = this.group?.id
      let groupPkid = positivePkid(this.group?.pkid)
      if (djangoId && groupPkid === null) {
        this.$bvToast.toast(
          'This group has no pkid. Reload the page and try again.',
          {
            title: 'Groups',
            variant: 'danger'
          }
        )

        return
      }
      if (djangoId) {
        await this.axios.put(
          endpoints.updateCustomerGroup(this.currentMerchant.id, djangoId),
          body
        )
      } else {
        groupPkid = positivePkid(
          (
            await this.axios.post(
              endpoints.createCustomerGroup(this.currentMerchant.id),
              body
            )
          ).data?.pkid
        )
        if (groupPkid === null) {
          this.$bvToast.toast(
            'The group was saved, but its automation was not. Reload the page and edit the group.',
            { title: 'Groups', variant: 'danger' }
          )

          return
        }
      }
      await cfAxios.put(
        endpoints.groupAutomation(this.currentMerchant.pkid, groupPkid),
        {
          template_id: templatePkid,
          is_active: this.group?.automation?.is_active ?? true,
          days_of_week: [...this.form.days].sort(),
          send_time: this.form.sendTime
        }
      )
      this.$emit('saved')
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? 'Failed to save the group'
      this.$bvToast.toast(message, { title: 'Groups', variant: 'danger' })
    } finally {
      this.saving = false
    }
  }
}
</script>

<style lang="scss" scoped>
$ink: #1f2937;
$line: #c7ced8;

// Same left edge as the toolbar above (.button-toolbar, scss/main.scss).
.group-form {
  margin: 0 25px 25px;
}

.group-card {
  padding: 32px 36px 24px;

  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  & + & {
    margin-top: 24px;
  }

  &--split {
    display: flex;
    gap: 48px;
  }
}

.group-card__half {
  flex: 1;
  min-width: 0;
}

.template-preview {
  max-width: 420px;

  .group-card__label {
    display: block;
  }
}

.template-preview__text {
  margin: 0 0 6px;
  padding: 12px 14px;
  color: $ink;

  font-size: 14px;
  white-space: pre-wrap;

  background: $background;
  border: 1px solid $line;
  border-radius: 6px;
}

.template-preview__note {
  color: $darker-gray;

  font-size: 12px;
}

.group-card__title {
  margin: 0;

  font-size: 22px;
  font-weight: 700;
}

.group-card__subtitle {
  margin: 7px 0 25px;
  color: $darker-gray;

  font-size: 14.5px;
}

.group-card__control {
  max-width: 420px;
  height: 47px;

  font-size: 15.5px;

  &--name {
    max-width: 560px;
  }

  &--time {
    max-width: 240px;
  }
}

.group-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
}

.group-footer__note {
  color: $darker-gray;

  font-size: 12px;
}

.group-footer__actions {
  display: flex;
  gap: 10px;
}

.schedule-presets,
.schedule-days {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.schedule-days {
  margin-bottom: 22px;
}

// Daily/Weekdays pills and the day circles share one look.
.schedule-chip {
  color: $ink;

  font-size: 14px;
  font-weight: 600;

  background: #fff;
  border: 1px solid $line;
  cursor: pointer;

  &--pill {
    padding: 8px 16px;

    border-radius: 999px;
  }

  &--day {
    width: 36px;
    height: 36px;

    border-radius: 50%;
  }

  &--active {
    color: #fff;

    background: $ink;
    border-color: $ink;
  }
}

.filter-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px 16px;

  background: #f3f5f8;
  border-radius: 8px;
}

.filter-row__keyword {
  width: 52px;
  color: $darker-gray;

  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.filter-row__field {
  width: 190px;
}

.filter-row__muted {
  color: $darker-gray;

  font-size: 14px;
}

.filter-row__values {
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
}

.filter-row__value {
  max-width: 140px;
}

.filter-row__remove,
.add-filter {
  background: transparent;
  border: 0;
  cursor: pointer;
}

.filter-row__remove {
  padding: 0 6px;
  color: $darker-gray;

  font-size: 20px;
  line-height: 1;
}

.add-filter {
  padding: 0;
  color: $ink;

  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
}

.match-count {
  margin: 20px 0 0;
  padding-top: 16px;
  color: $darker-gray;

  font-size: 14px;

  border-top: 1px solid #e5e8ee;

  strong {
    color: $ink;
  }
}
</style>

<style lang="scss">
// Not scoped: b-form-group renders the label outside this component's scope id.
.group-card__label {
  margin-bottom: 9px;
  color: #1f2937;

  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
</style>
